import React, { useState } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import Text, { TEXT_COLOR, TEXT_SEGMENT } from "@/bit/components/components.text";
import Glyph from "@/bit/components/components.icon-glyph";
import CustomLink from "@/bit/components/components.custom-link";
// import emptyContent from "./emptyContent.json";

const CmsEditableGroupIcons = dynamic(() =>
  import("@/bit/components/components.cms-block.cms-editable-group-icons")
);
const CmsAddElement = dynamic(() =>
  import("@/bit/components/components.cms-block.cms-add-element")
);

export const COMPONENT_NAME = "Faqs";
export { TEXT_COLOR, TEXT_SEGMENT };
const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;

const ID_TEMPLATE_ORION = "253895";

/**
 * Componente que permite crear una lista desplegable de FAQs.
 */
const Faqs = ({
  openAllQuestions = false,
  iconColor = TEXT_COLOR.BLUE,
  titleColorClosed = TEXT_COLOR.GREY5,
  borderBottomOnly = true,
  className = "",
  cmsHandler = null,
  faqsList,
  blockName,
  alias,
}) => {
  const [openIndices, setOpenIndices] = useState(
    openAllQuestions ? faqsList.map((_, index) => index) : []
  );

  const toggleQuestion = (index) => {
    setOpenIndices((prevOpenIndices) =>
      prevOpenIndices.includes(index)
        ? prevOpenIndices.filter((i) => i !== index)
        : [...prevOpenIndices, index]
    );
  };

  const TAGGING_INFO = {
    componentName: COMPONENT_NAME,
    componentContent: "",
    componentProduct: "",
    componentVariation: "none",
    eventType: "click",
  };

  // Cuando no hay elementos
  if (
    (currentEnv === "qa_ssr" || currentEnv === "local") &&
    faqsList &&
    Array.isArray(faqsList) &&
    faqsList.length === 0
  ) {
    return (
      <>
        <div className={`faq-item`}>
          <div className={`faq-title`}>
            <Text
              text="cms-atom-text-1"
              // {...emptyContent["cms-atom-text-1"]}
              cmsHandler={cmsHandler}
              className="text-content"
            />
            <span>
              <Glyph id="cheuron-up-m" color={iconColor} width={16} />
            </span>
          </div>
        </div>
        {cmsHandler && (
          <CmsAddElement
            addProps={{
              index: "start",
              idTemplateOrion: ID_TEMPLATE_ORION,
              itemIdDotNotation: `${blockName}.cms-ml-single-faq-list`,
              alias,
              layout: "vertical",
            }}
            cmsHandler={cmsHandler}
          />
        )}
      </>
    );
  }

  return (
    <section className={`faqs ${className}`}>
      {cmsHandler && (
        <CmsAddElement
          addProps={{
            index: "start",
            idTemplateOrion: ID_TEMPLATE_ORION,
            itemIdDotNotation: `${blockName}.cms-ml-single-faq-list`,
            alias,
            layout: "vertical",
          }}
          cmsHandler={cmsHandler}
        />
      )}
      {faqsList &&
        Array.isArray(faqsList) &&
        faqsList.length > 0 &&
        faqsList.map((faq, index) => {
          const titleProps = faq["cms-atom-text-1"];
          const answerProps = faq["cms-atom-text-2"];
          const taggingInfo = {
            ...TAGGING_INFO,
            componentProduct: `faqItem-${index}`,
            componentContent: openIndices.includes(index)
              ? "faq-plegar"
              : "faq-desplegar",
            componentVariation: "none",
            eventType: "click",
          };
          const classNameCC = `faq-title ${
            openIndices.includes(index) ? "open" : ""
          }`;
          const classNameAnswer = `faq-description ${
            openIndices.includes(index) ? "open" : ""
          }`;
          const color = openIndices.includes(index)
            ? titleProps.color
            : titleColorClosed;
          const borderBottomOnlyClassName = borderBottomOnly
            ? "faq-item--with-bottom-border"
            : "faq-item--with-borders";

          return (
            <div
              key={`faq-item--${index}`}
              className={`faq-item ${borderBottomOnlyClassName}`}
            >
              {/* Bolitas de edicion aqui */}
              <CmsEditableGroupIcons
                elementProps={{}}
                deleteProps={{
                  index,
                  itemIdDotNotation: `${blockName}.cms-ml-single-faq-list`,
                  alias,
                  frontalListToDelete: faqsList,
                }}
                visibleTools={{
                  hiring: false,
                  edit: false,
                  deleteItem: true,
                }}
                cmsHandler={cmsHandler}
              />
              <CustomLink
                title={titleProps?.title}
                ariaLabel={titleProps?.title}
                taggingInfo={taggingInfo}
                onClick={() => toggleQuestion(index)}
              >
                <div className={classNameCC}>
                  <Text
                    {...titleProps}
                    cmsHandler={cmsHandler}
                    color={color}
                    className="text-content"
                  />
                  <span>
                    <Glyph id="cheuron-up-m" color={iconColor} width={16} />
                  </span>
                </div>
              </CustomLink>
              <div className={classNameAnswer}>
                <div className="text">
                  <Text {...answerProps} cmsHandler={cmsHandler} />
                </div>
              </div>
              <style jsx>{`.faq-item {
  position: relative;
  &--with-borders {
    border: 1px solid #eeeeee;
  }
  &--with-bottom-border {
    border-bottom: 1px solid #eeeeee;
    &:hover {
      background-color: #f5f5f5;
    }
  }
  &__content--opened > span,
  p {
    line-height: 1.5;
  }

  :global(.next-link) {
    width: 100%;
  }
  .faq-title {
    padding: 1rem 5px;
    background-color: transparent;
    width: 100%;
    outline-color: transparent;
    outline-width: 0px;
    position: relative;
    display: flex;
    justify-content: space-between;
    span {
      transform: rotate(180deg);
      -webkit-transition: all 0.3s ease-out;
      -moz-transition: all 0.3s ease-out;
      -o-transition: all 0.3s ease-out;
      transition: all 0.3s ease-out;
    }
    &.open span {
      transform: rotate(0deg);
      -webkit-transition: all 0.3s ease-out;
      -moz-transition: all 0.3s ease-out;
      -o-transition: all 0.3s ease-out;
      transition: all 0.3s ease-out;
    }
  }
  .faq-description {
    max-height: 0px;
    overflow: hidden;
    transition: all 0.5s ease-out;
    .text {
        padding: 1rem;
        padding-top: 0px;
      }
    &.open {
      transition: all 1s ease-out;
      max-height: 800px;              
    }              
    :global(a) {
      color: #0079bb;
      &:hover {
        opacity: 0.8;
      }
    }
  }
}`}</style>
            </div>
          );
        })}
      {cmsHandler && (
        <CmsAddElement
          addProps={{
            index: "end",
            idTemplateOrion: ID_TEMPLATE_ORION,
            itemIdDotNotation: `${blockName}.cms-ml-single-faq-list`,
            alias,
            layout: "vertical",
          }}
          cmsHandler={cmsHandler}
        />
      )}
    </section>
  );
};

Faqs.propTypes = {
  /**
   * {
   *  "info": "Booleano que nos permite abrir todas las preguntas por defecto",
   *  "kind": "both",
   *  "beautifulName": "Expandido por defecto"
   * }
   */
  openAllQuestions: PropTypes.bool,
  /**
   * Listado de preguntas frecuentes/faqs
   */
  /**
   * {
   *  "info": "Listado de preguntas frecuentes/faqs",
   *  "kind": "both",
   *  "beautifulName": "Lista de elementos"
   * }
   */
  listadoFAQS: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  /**
   * {
   *  "info": "Color del icono",
   *  "kind": "both",
   *  "beautifulName": "Color del icono"
   * }
   */
  iconColor: PropTypes.string,
  /**
   * {
   *  "info": "Color de los títulos de las faqs abiertas",
   *  "kind": "both",
   *  "beautifulName": "Color del texto abierto"
   * }
   */
  titleColorOpen: PropTypes.string,
  /**
   * {
   *  "info": "Color de los títulos de las faqs cerradas",
   *  "kind": "both",
   *  "beautifulName": "Color del texto cerrado"
   * }
   */
  titleColorClosed: PropTypes.string,
  /**
   * {
   *  "info": "Faqs con border o no",
   *  "kind": "both",
   *  "beautifulName": "Borde de faqs"
   * }
   */
  borderBottomOnly: PropTypes.bool,
  /**
   * {
   *  "info": "Necesita para especificar el segmento",
   *  "kind": "both",
   *  "beautifulName": "Segmento"
   * }
   */
  segment: PropTypes.string,
  /**
   * {
   *  "info": "Classnames que heredará el componente, sirve para aplicar estilos",
   *  "kind": "both",
   *  "beautifulName": "ClassName"
   * }
   */
  className: PropTypes.string,
};

export default Faqs;
